import { type ComponentProps, useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import { useTableContext } from "../table.context"
import type { ColumnFilterConfig } from "../table.types"

export type TableFilterButtonProps = ComponentProps<"button">

/**
 * Filter toggle button. Opens the inline filter panel below the toolbar.
 */
function TableFilterButton(props: TableFilterButtonProps) {
  const { className, onClick, ...buttonProps } = props
  const { table, activePanel, setActivePanel } = useTableContext()

  // Keyed on the column list (see TableToolbarFilters), so a definition
  // change after mount is counted.
  const allColumns = table.getAllColumns()
  const filterableColumns = useMemo(() => {
    return allColumns.filter((column) => {
      if (column.id === "__select") return false
      const meta = column.columnDef.meta as
        | { filter?: ColumnFilterConfig }
        | undefined
      return !!meta?.filter && !meta.filter.toolbar
    })
  }, [allColumns])

  const { columnFilters } = table.getState()
  const panelColumnIds = new Set(filterableColumns.map((c) => c.id))
  const activeFilterCount = columnFilters.filter((f) =>
    panelColumnIds.has(f.id),
  ).length
  const isActive = activePanel === "filter"

  if (filterableColumns.length === 0) return null

  return (
    <Button
      variant={isActive || activeFilterCount > 0 ? "secondary" : "ghost"}
      size="xs"
      iconSrc="filter"
      iconClassName="ui-table-icon"
      className={cn("ui-table-filter-button", className)}
      onClick={(e) => {
        setActivePanel(isActive ? null : "filter")
        onClick?.(e)
      }}
      {...buttonProps}
    >
      <span>Filter</span>
      {activeFilterCount > 0 && (
        <span className="ui-table-filter-badge">{activeFilterCount}</span>
      )}
    </Button>
  )
}

export default TableFilterButton
